// app/src/types.ts

export type CountryLabels = {
	de: string;
	en: string;
	es: string;
	fr: string;
	it: string;
	nl: string;
	pl: string;
	pt: string;
	ro: string;
};

export type Country = {
	label: CountryLabels;
	countryCode: string;
};

export interface ContactInfo {
	profile: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	function: string;
	howDidYouKnowUs: string;
}

export interface CompanyInfo {
	countryCode: string;
	companyName: string;
	nbtva: string;
	siret: string;
	address: string;
	postalCode: string;
	siren: string;
	city: string;
}

export interface ReceiveRecommendation {
	email: boolean;
	sms: boolean;
}

interface DocumentFile {
	name: string;
	data: string;
}

export interface DocumentsInfo {
	companyRegistration: DocumentFile[];
	identityCard: DocumentFile[];
	identityCardBack: DocumentFile[];
	other: DocumentFile[];
}

export type ExistingCompanyMatch = {
	companyID: string;
	roles: number;
};

export type CompanyLookupTelemetry = {
	lookupMode?: 'reduced' | 'classic';
	countryCode?: string;
	identifierType?: 'vat';
};

export interface UserInfo {
	contact: ContactInfo;
	company: CompanyInfo;
	receiveRecommendation: ReceiveRecommendation;
	documents: DocumentsInfo;
}

export interface AuthContextProps {
	loadingSignin: boolean;
	loadingSignup: boolean;
	userInfo: UserInfo;
	loadUserInfoFromLocalStorage: (email: string) => void;
	openedPanel: string;
	isFirstPanelValidated: boolean;
	isSecondPanelValidated: boolean;
	isThirdPanelValidated: boolean;
	shouldSkipDocumentUploadStep: boolean;
	handleUserAuthentication: (email: string, userType: UserType) => Promise<void>;
	handleSignUpPro: () => Promise<void>;
	handleSignUpPart: () => Promise<void>;
	setUserInfo: React.Dispatch<React.SetStateAction<UserInfo>>;
	setOpenedPanel: React.Dispatch<React.SetStateAction<string>>;
	validateFirstPanel: () => void;
	validateSecondPanel: () => Promise<void>;
	syncExistingCompanyMatchByVAT: (
		vatNumber?: string,
		telemetry?: CompanyLookupTelemetry
	) => Promise<ExistingCompanyMatch | null>;
	activateExistingCompanyAttachment: (match: ExistingCompanyMatch) => void;
	validateThirdPanel: () => void;
	skipThirdPanel: () => void;
}

export enum UserType  {
	PRO = 'pro',
	PART = 'part'
}

export type AuthFlow  = 'b2c' | 'b2b';

export type MicrosoftAuthMode = 'private' | 'pro';
export type MicrosoftAuthType = 'signin' | 'signup';

export interface MicrosoftAuthStateInput {
	readonly redirectionUrl: string;
	readonly targetPage: string;
	readonly mode?: MicrosoftAuthMode;
	readonly type?: MicrosoftAuthType;
}

export interface MicrosoftAuthStateOutput {
	readonly redirectionUrl: string;
	readonly targetPage: string;
	readonly mode: MicrosoftAuthMode;
	readonly type: MicrosoftAuthType;
}

export interface UrlContextProps {
	redirectToContactUsPro: () => void;
	redirectToContactUsPart: () => void;
	redirectToHomePro : () => void;
	redirectToHomePart : () => void;
	redirectToProAfterSignup: () => void;
	redirectToPartAfterSignup: () => void;
	redirectToMicrosoftAuth: (
		flow: AuthFlow ,
		email: string,
		state?: Partial<MicrosoftAuthStateInput>,
	) => void;
	microsoftAuthState: Partial<MicrosoftAuthStateOutput> | null;
	goBackWithFallback: () => void;
}

export interface AuthConfig {
	clientId: string;
	policy: string;
	scope: string;
}
